import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAnnualSalary,
  setMonthlyContribution,
  setMonthlyRate,
  setCurrentAge,
  setRetirementAge,
  setReturnRate,
  setCurrentBalance,
  setEmployerMatch,
  setSalaryLimit,
  setEstimatedReturn,
  resetContributionData,
  setCalculationResults,
} from "../redux/calculations/contributionSlice";
import {
  resetChartData,
  setContributionCartCalc,
  updatedContributionChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const ContributionCalc = ({ setShowChart }) => {
  const { textData } = useLocale();

  const dispatch = useDispatch();
  const {
    annualSalary,
    monthlyContribution,
    currentAge,
    retirementAge,
    returnRate,
    currentBalance,
    employerMatch,
    salaryLimit,
  } = useSelector((state) => state.contributionCalculator);
  const [calcDone, setCalcDone] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const [showTable, setShowTable] = useState(false);
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      annualSalary: annualSalary,
      monthlyContribution: monthlyContribution,
      currentAge: currentAge,
      retirementAge: retirementAge,
      returnRate: returnRate,
      currentBalance: currentBalance,
      employerMatch: employerMatch,
      salaryLimit: salaryLimit,
    },
    validationSchema: Yup.object({
      annualSalary: Yup.number()
        .min(100000, textData.errorMinSalary)
        .max(30000000, textData.errorMinSalary)
        .required(textData.errorSalaryRequired),
      monthlyContribution: Yup.number()
        .min(0, textData.errorMinContribution)
        .max(50, textData.errorMinContribution)
        .required(textData.errorContributionRequired)
        .test(
          "contribution-less-than-salary",
          textData.errorContributionExceedsSalary,
          function (value) {
            const { annualSalary } = this.parent;
            return value <= annualSalary / 12;
          }
        ),

      currentAge: Yup.number()
        .min(18, textData.errorAgeRange)
        .max(65, textData.errorAgeRange)
        .required(textData.errorAgeRequired),
      retirementAge: Yup.number()
        .min(40, textData.errorRetirementAgeRange)
        .max(75, textData.errorRetirementAgeRange)
        .required(textData.errorRetirementAgeRequired)
        .test(
          "retirement-greater-than-current",
          textData.errorRetirementAgeGreater,
          function (value) {
            const { currentAge } = this.parent;
            return value > currentAge;
          }
        ),
      returnRate: Yup.number()
        .min(1, textData.errorReturnRate)
        .max(20, textData.errorReturnRate)
        .required(textData.errorReturnRateRequired),
      currentBalance: Yup.number()
        .min(0, textData.errorCurrentBalance)
        .max(30000000, textData.errorCurrentBalance)
        .required(textData.errorCurrentBalanceRequired),
      employerMatch: Yup.number()
        .min(0, textData.errorEmployerMatch)
        .max(20, textData.errorEmployerMatch)
        .required(textData.errorEmployerMatchRequired),
      salaryLimit: Yup.number()
        .min(0, textData.errorSalaryLimit)
        .max(10, textData.errorSalaryLimit)
        .required(textData.errorSalaryLimitRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_CONTRIBUTION");
      }
      dispatch(setContributionCartCalc(true));
      try {
        // Step 1: Convert inputs to numbers
        const salary = Number(values.annualSalary);
        const contributionRate = Number(values.monthlyContribution) / 100;
        const age = Number(values.currentAge);
        const retireAge = Number(values.retirementAge);
        const returnRate = Number(values.returnRate);
        const balance = Number(values.currentBalance);
        const matchRate = Number(values.employerMatch) / 100;
        const matchLimit = Number(values.salaryLimit) / 100;
        const retirementYears = 20;

        // Step 2: Validation
        const inputs = [
          salary,
          contributionRate,
          age,
          retireAge,
          returnRate,
          balance,
          matchRate,
          matchLimit,
          retirementYears,
        ];
        if (
          inputs.some(isNaN) ||
          inputs.some((val) => val < 0) ||
          retireAge <= age
        ) {
          return; // Handle validation error
        }

        // Step 3: Setup variables
        const yearsToGrow = retireAge - age;
        const months = yearsToGrow * 12;
        const monthlyReturn = Number((returnRate / (12 * 100)).toFixed(6));

        const monthlyEmployee = Number(
          ((salary * contributionRate) / 12).toFixed(2)
        );
        const monthlyEmployer = Number(
          ((salary * matchLimit * matchRate) / 12).toFixed(2)
        );
        const monthlyTotal = monthlyEmployee + monthlyEmployer;

        const ONE_PLUS_RN = Number(
          Math.pow(1 + monthlyReturn, months).toFixed(3)
        );

        // Step 4: Future value calculations
        const futureValue =
          balance * ONE_PLUS_RN +
          Number(
            ((monthlyTotal * (ONE_PLUS_RN - 1)) / monthlyReturn).toFixed(2)
          );

        const futureValueWithoutEmployer =
          balance * ONE_PLUS_RN +
          Number(
            ((monthlyEmployee * (ONE_PLUS_RN - 1)) / monthlyReturn).toFixed(2)
          );

        const retirementMonths = retirementYears * 12;
        const monthlyRetirementAmount = Number(
          (futureValue / retirementMonths).toFixed(2)
        );

        // Step 5: Chart data and report
        const dataPoints = [];
        const dataPointsNoMatch = [];
        let balanceWithMatch = balance;
        let balanceWithoutMatch = balance;

        for (let year = 1; year <= yearsToGrow; year++) {
          for (let month = 1; month <= 12; month++) {
            balanceWithMatch =
              balanceWithMatch * (1 + monthlyReturn) + monthlyTotal;
            balanceWithoutMatch =
              balanceWithoutMatch * (1 + monthlyReturn) + monthlyEmployee;
          }

          const currentAgeYear = age + year;

          dataPoints.push({
            x: currentAgeYear,
            y: Math.round(balanceWithMatch),
          });
          dataPointsNoMatch.push({
            x: currentAgeYear,
            y: Math.round(balanceWithoutMatch),
          });
        }

        const totalEmployeeContributions = monthlyEmployee * months;
        const totalEmployerContributions = monthlyEmployer * months;

        // Step 6: Dispatch
        dispatch(setAnnualSalary(values.annualSalary));
        dispatch(setMonthlyContribution(values.monthlyContribution));
        dispatch(setCurrentAge(values.currentAge));
        dispatch(setRetirementAge(values.retirementAge));
        dispatch(setReturnRate(values.returnRate));
        dispatch(setCurrentBalance(values.currentBalance));
        dispatch(setEmployerMatch(values.employerMatch));
        dispatch(setSalaryLimit(values.salaryLimit));
        dispatch(setEstimatedReturn(futureValue));
        dispatch(setMonthlyRate(monthlyRetirementAmount));
        dispatch(setCalculationResults({
          withEmployerMatch: Math.round(futureValue),
          withoutEmployerMatch: Math.round(futureValueWithoutEmployer),
          monthlyRetirementAmount: monthlyRetirementAmount,
          totalEmployeeContributions: Math.round(totalEmployeeContributions),
          totalEmployerContributions: Math.round(totalEmployerContributions),
          chartDataWithMatch: dataPoints,
          chartDataWithoutMatch: dataPointsNoMatch,
        }));

        dispatch(
          updatedContributionChart({
            withEmployerMatch: Math.round(futureValue),
            withoutEmployerMatch: Math.round(futureValueWithoutEmployer),
            monthlyRetirementAmount: monthlyRetirementAmount,
            totalEmployeeContributions: Math.round(totalEmployeeContributions),
            totalEmployerContributions: Math.round(totalEmployerContributions),
            chartDataWithMatch: dataPoints,
            chartDataWithoutMatch: dataPointsNoMatch,
          })
        );

        saveToHistory({
          tabName: "contributionCalc",
          salary: values.annualSalary,
          contributionRate: values.monthlyContribution,
          currentAge: values.currentAge,
          retirementAge: values.retirementAge,
          returnRate: values.returnRate,
          currentBalance: values.currentBalance,
          employerMatch: values.employerMatch,
          salaryLimit: values.salaryLimit,
          estimatedReturn: futureValue,
          totalSalary: salary,
          totalContribution: contributionRate,
          age: age,
          retiremenetAge: retireAge,
          totalReturn: returnRate,
          balance: balance,
          matchRate: matchRate,
          matchLimit: matchLimit,
          retirementYears: retirementYears,
          calculationType: "Contribution",
        });

        setShowChart(true);
      } catch (error) {
        console.error("Calculation error:", error);
      }
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetContributionData());
    };
  }, [dispatch]);
  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        salary: calculationData.salary,
        contributionRate: calculationData.contributionRate,
        currentAge: calculationData.currentAge,
        retirementAge: calculationData.retirementAge,
        returnRate: calculationData.returnRate,
        currentBalance: calculationData.currentBalance,
        employerMatch: calculationData.employerMatch,
        salaryLimit: calculationData.salaryLimit,
        estimatedReturn: calculationData.estimatedReturn,
        calculationType: calculationData.calculationType,
        totalSalary: calculationData.totalSalary,
        totalContribution: calculationData.totalContribution,
        age: calculationData.age,
        retiremenetAge: calculationData.retiremenetAge,
        totalReturn: calculationData.totalReturn,
        balance: calculationData.balance,
        matchRate: calculationData.matchRate,
        matchLimit: calculationData.matchLimit,
        retirementYears: calculationData.retirementYears,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.salary === newHistoryRecord.salary &&
          record.contributionRate === newHistoryRecord.contributionRate &&
          record.currentAge === newHistoryRecord.currentAge &&
          record.retirementAge === newHistoryRecord.retirementAge &&
          record.returnRate === newHistoryRecord.returnRate &&
          record.currentBalance === newHistoryRecord.currentBalance &&
          record.employerMatch === newHistoryRecord.employerMatch &&
          record.salaryLimit === newHistoryRecord.salaryLimit &&
          record.estimatedReturn === newHistoryRecord.estimatedReturn
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  const handleSalaryChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("annualSalary", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 13) return;

    if (rawValue === "0") {
      formik.setFieldValue("annualSalary", 0);
      return;
    }
    formik.setFieldValue("annualSalary", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";
    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(i18n.language || "en-IN");
  };

  const handleContributionChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("monthlyContribution", rawValue);
      dispatch(setMonthlyContribution(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("monthlyContribution", normalizedValue);
    dispatch(setMonthlyContribution(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleReturnRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("returnRate", rawValue);
      dispatch(setReturnRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("returnRate", normalizedValue);
    dispatch(setReturnRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleEmployeMatchChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("employerMatch", rawValue);
      dispatch(setEmployerMatch(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("employerMatch", normalizedValue);
    dispatch(setEmployerMatch(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleSalarylimitChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("salaryLimit", rawValue);
      dispatch(setSalaryLimit(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("salaryLimit", normalizedValue);
    dispatch(setSalaryLimit(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const salarySliderRef = useRef(null);
  const contributionSliderRef = useRef(null);
  const currentAgeSliderRef = useRef(null);
  const retirementAgeSliderRef = useRef(null);
  const returnRateSliderRef = useRef(null);
  const currentBalanceSliderRef = useRef(null);
  const employerMatchSliderRef = useRef(null);
  const salaryLimitSliderRef = useRef(null);

  const handleBalanceChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("currentBalance", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 14) return;
    if (rawValue === "0") {
      formik.setFieldValue("currentBalance", 0);
      return;
    }
    formik.setFieldValue("currentBalance", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleAnnualSalarySliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setAnnualSalary(value));
    formik.setFieldValue("annualSalary", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleMonthlyContributionSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMonthlyContribution(value));
    formik.setFieldValue("monthlyContribution", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleCurrentAgeSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setCurrentAge(value));
    formik.setFieldValue("currentAge", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleRetirementAgeSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setRetirementAge(value));
    formik.setFieldValue("retirementAge", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleReturnRateInputChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "" || inputValue === null || inputValue === undefined) {
      dispatch(setReturnRate(""));
      formik.setFieldValue("returnRate", "");
      return;
    }
    const value = Number(inputValue);
    if (!isNaN(value)) {
      dispatch(setReturnRate(value));
      formik.setFieldValue("returnRate", value);
    } else {
      formik.setFieldValue("returnRate", inputValue);
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleReturnRateSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setReturnRate(value));
    formik.setFieldValue("returnRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleCurrentBalanceSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setCurrentBalance(value));
    formik.setFieldValue("currentBalance", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleEmployerMatchSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setEmployerMatch(value));
    formik.setFieldValue("employerMatch", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleSalaryLimitSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setSalaryLimit(value));
    formik.setFieldValue("salaryLimit", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  useEffect(() => {
    formik.setValues({
      annualSalary: annualSalary,
      monthlyContribution: monthlyContribution,
      currentAge: currentAge,
      retirementAge: retirementAge,
      returnRate: returnRate,
      currentBalance: currentBalance,
      employerMatch: employerMatch,
      salaryLimit: salaryLimit,
    });
  }, [
    annualSalary,
    monthlyContribution,
    currentAge,
    retirementAge,
    returnRate,
    currentBalance,
    employerMatch,
    salaryLimit,
  ]);

  useEffect(() => {
    dispatch(setAnnualSalary(formik.values.annualSalary));
    dispatch(setMonthlyContribution(formik.values.monthlyContribution));
    dispatch(setCurrentAge(formik.values.currentAge));
    dispatch(setRetirementAge(formik.values.retirementAge));
    dispatch(setCurrentBalance(formik.values.currentBalance));
    dispatch(setEmployerMatch(formik.values.employerMatch));
    dispatch(setSalaryLimit(formik.values.salaryLimit));
    dispatch(setEstimatedReturn(formik.values.returnRate));
  }, [formik.values, dispatch]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.contrubutionContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.contrubutionContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.contrubutionContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.contrubutionContent.howItWorksDescription}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        <strong>{textData.contrubutionContent.fvFormulaLabel}</strong>{" "}
      </p>

      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.contrubutionContent.symbolTable.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.contrubutionContent.symbolTable.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              FV
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.contrubutionContent.symbolTable.FV}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.contrubutionContent.symbolTable.P}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.contrubutionContent.symbolTable.r}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.contrubutionContent.symbolTable.n}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              PMT
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.contrubutionContent.symbolTable.PMT}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.contrubutionContent.featuresTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.contrubutionContent.empMatch}</strong>{" "}
          {textData.contrubutionContent.featuresList[0]}
        </li>
        <li>
          <strong>{textData.contrubutionContent.taxMatch}</strong>
          {textData.contrubutionContent.featuresList[1]}
        </li>
        <li>
          <strong>{textData.contrubutionContent.vestPeriod}</strong>{" "}
          {textData.contrubutionContent.featuresList[2]}
        </li>
        <li>
          <strong>{textData.contrubutionContent.contribuionLimit}</strong>{" "}
          {textData.contrubutionContent.featuresList[3]}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.contrubutionContent.withdrawalRulesTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.contrubutionContent.withdrawalRulesDescription}
      </p>
      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.contrubutionContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        {" "}
        <li>{textData.contrubutionContent.benefitsList[0]}</li>
        <li>{textData.contrubutionContent.benefitsList[1]}</li>
        <li> {textData.contrubutionContent.benefitsList[2]}</li>
        <li> {textData.contrubutionContent.benefitsList[3]}</li>
        <li>{textData.contrubutionContent.benefitsList[4]}</li>
      </ul>
    </div>
  );
  const getFormattedPlaceholder = (value = 10000, language) => {
    return new Intl.NumberFormat(language).format(value);
  };

  const timeLabel = t("yearShort");

  return (
    <>
      <Helmet>
        <title>Ready to Retire? Plan with Our Free 401k Calculator</title>
        <meta
          name="description"
          content="Plan your retirement with our free 401k Contribution Calculator. Instantly estimate savings, see if you’re on track, and secure your financial future."
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/401k-retirement-calculator"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Ready to Retire? Plan with Our Free 401k Calculator"
        />
        <meta
          property="og:description"
          content="Plan your retirement with our free 401k Contribution Calculator. Instantly estimate savings, see if you’re on track, and secure your financial future."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/401k-retirement-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="401k Calculator to Plan Retirement Savings"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            {textData.retirementTitle}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {" "}
              {textData.savingsTitle}
            </span>
          </>
        }
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        description={textData.retirementDescription}
        descriptionContent={descriptionContent}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            {/* Annual Salary */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.annualSalary}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={salarySliderRef}>
                  
                  <input
                    type="range"
                    min={100000}
                    max={30000000}
                    step={5000}
                    value={annualSalary}
                    onChange={handleAnnualSalarySliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((annualSalary - 100000) / (30000000 - 100000)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="annualSalary"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.annualSalary)}
                    onChange={handleSalaryChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.annualSalary && formik.errors.annualSalary
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${
                      formik.touched.annualSalary && formik.errors.annualSalary
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.annualSalary &&
                    formik.errors.annualSalary && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.annualSalary}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Monthly Contribution */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.contributionRate}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={contributionSliderRef}>
                  
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={parseFloat(formik.values.monthlyContribution)}
                    onChange={handleMonthlyContributionSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.monthlyContribution - 0) / (50 - 0)) *
                        100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="monthlyContribution"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("7.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.monthlyContribution
                    )}
                    onChange={handleContributionChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.monthlyContribution &&
                      formik.errors.monthlyContribution
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${
                      formik.touched.monthlyContribution &&
                      formik.errors.monthlyContribution
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.monthlyContribution &&
                    formik.errors.monthlyContribution && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.monthlyContribution}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Current Age */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.age}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={currentAgeSliderRef}>
                  
                  <input
                    type="range"
                    min={18}
                    max={65}
                    step={1}
                    value={currentAge}
                    onChange={handleCurrentAgeSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((currentAge - 18) / (65 - 18)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="currentAge"
                    leftIcon={timeLabel}
                    value={formik.values.currentAge}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,2}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"30"}
                    borderColor={
                      formik.touched.currentAge && formik.errors.currentAge
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]   ${
                      formik.touched.currentAge && formik.errors.currentAge
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.currentAge && formik.errors.currentAge && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.currentAge}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Retirement Age */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.retirementAge}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={retirementAgeSliderRef}
                >
                  
                  <input
                    type="range"
                    min={40}
                    max={75}
                    step={1}
                    value={retirementAge}
                    onChange={handleRetirementAgeSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((retirementAge - 40) / (75 - 40)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="retirementAge"
                    leftIcon={timeLabel}
                    value={formik.values.retirementAge}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,2}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"65"}
                    borderColor={
                      formik.touched.retirementAge &&
                      formik.errors.retirementAge
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]    ${
                      formik.touched.retirementAge &&
                      formik.errors.retirementAge
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.retirementAge &&
                    formik.errors.retirementAge && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.retirementAge}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Expected Rate of Return */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.expectedReturn}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={returnRateSliderRef}>
                  
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={1}
                    value={parseFloat(formik.values.returnRate)}
                    onChange={handleReturnRateSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.returnRate - 1) / (20 - 1)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="returnRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("7.0")}
                    value={DecimalUtils.formatDisplay(formik.values.returnRate)}
                    onChange={handleReturnRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.returnRate && formik.errors.returnRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${
                      formik.touched.returnRate && formik.errors.returnRate
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.returnRate && formik.errors.returnRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.returnRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Current 401(k) Balance */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.currentBalance}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={currentBalanceSliderRef}
                >
                  
                  <input
                    type="range"
                    min={0}
                    max={30000000}
                    step={1000}
                    value={currentBalance}
                    onChange={handleCurrentBalanceSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((currentBalance - 0) / (30000000 - 0)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="currentBalance"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.currentBalance)}
                    onChange={handleBalanceChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(5000, i18n.language)}
                    borderColor={
                      formik.touched.currentBalance &&
                      formik.errors.currentBalance
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]    ${
                      formik.touched.currentBalance &&
                      formik.errors.currentBalance
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.currentBalance &&
                    formik.errors.currentBalance && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.currentBalance}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Employer Match */}
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.employerMatch}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={employerMatchSliderRef}
                >
                  
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={1}
                    value={parseFloat(employerMatch)}
                    onChange={handleEmployerMatchSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${(employerMatch / 20) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="employerMatch"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("20")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.employerMatch
                    )}
                    onChange={handleEmployeMatchChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.employerMatch &&
                      formik.errors.employerMatch
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    ${
                      formik.touched.employerMatch &&
                      formik.errors.employerMatch
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.employerMatch &&
                    formik.errors.employerMatch && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.employerMatch}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Salary Limit for Employer Match */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.salaryLimit}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={salaryLimitSliderRef}>
                  
                  <input
                    type="range"
                    min={0}
                    max={10}
                    step={1}
                    value={parseFloat(salaryLimit)}
                    onChange={handleSalaryLimitSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${(salaryLimit / 10) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="salaryLimit"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.salaryLimit
                    )}
                    onChange={handleSalarylimitChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.salaryLimit && formik.errors.salaryLimit
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]    ${
                      formik.touched.salaryLimit && formik.errors.salaryLimit
                        ? "border-danger"
                        : "border-gray-light"
                    }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.salaryLimit && formik.errors.salaryLimit && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.salaryLimit}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default ContributionCalc;
